<template>
  <v-form ref="formRef" v-model="formValid" @submit.prevent>
    <v-alert v-if="isMetaLoading" type="info" variant="tonal" density="compact" class="mb-4">
      <v-progress-circular indeterminate size="16" width="2" class="mr-2" />
      {{ tl('Loading Name Search options...') }}
    </v-alert>
    <v-alert v-else-if="metaLoadError" type="error" variant="tonal" density="compact" class="mb-4">
      {{ tl(metaLoadError) }}
      <v-btn size="small" variant="text" class="ml-2" @click="loadNameSearchMeta">{{ tl('Retry') }}</v-btn>
    </v-alert>
    <v-expansion-panels v-model="expandedPanels" multiple>
      <v-expansion-panel
        v-for="section in panelSections"
        v-show="section.id === 'details' || nameGateOpen"
        :key="section.id"
        :value="section.id"
      >
        <v-expansion-panel-title class="bg-primary text-white">
          <template v-if="section.type === 'dynamic-list'">
            <div class="d-flex justify-space-between align-center" style="width: 100%;">
              <div>
                <v-icon size="small" class="mr-2">{{ section.icon }}</v-icon>
                {{ tl(section.title) }} <span v-if="section.required" class="text-error">*</span>
                <span class="text-caption ml-1 font-weight-regular">
                  ({{ tl('Min') }} {{ promoterMin }}, {{ tl('Max') }} {{ MAX_PROMOTERS }})
                </span>
                <v-chip v-if="formData.promoters.length > 0" size="small" class="ml-2" color="white" variant="outlined">
                  {{ formData.promoters.length }}
                </v-chip>
              </div>
              <v-btn
                v-if="!props.readOnly && formData.promoters.length < MAX_PROMOTERS"
                color="white"
                variant="text"
                size="small"
                @click.stop="addPromoter"
              >
                <v-icon start>mdi-plus</v-icon>
                {{ tl(section.addLabel || 'Add') }}
              </v-btn>
            </div>
          </template>
          <template v-else>
            <v-icon size="small" class="mr-2">{{ section.icon }}</v-icon>
            {{ tl(section.title) }}
            <span v-if="section.subtitle" class="text-caption ml-2 font-weight-regular">({{ tl(section.subtitle) }})</span>
          </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <!-- Standard sections use SectionRenderer (details section waits for name-search-meta) -->
          <SectionRenderer
            v-if="section.type === 'standard' && (section.id !== 'details' || nsMeta)"
            :section="section"
            :form-data="formData"
            :readonly="props.readOnly"
            :ref-data="dynamicRefData"
            density="comfortable"
            :contacts="contacts"
            :contacts-loading="isLoadingContacts"
            @update:field="handleFieldUpdate"
          />

          <!-- Name availability check (details section only) -->
          <div v-if="section.id === 'details' && !props.readOnly && nsMeta" class="mt-2">
            <v-btn
              color="primary"
              variant="tonal"
              :loading="nameCheck.status === 'checking'"
              :disabled="!canCheckAvailability"
              @click="checkAvailability"
            >
              <v-icon start>mdi-magnify</v-icon>
              {{ tl('Check Availability') }}
            </v-btn>

            <v-alert
              v-if="nameCheck.status === 'available' && nameVerified"
              type="success"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              {{ tl('Your proposed name is available. You may proceed.') }}
            </v-alert>
            <v-alert
              v-else-if="nameCheck.status === 'unavailable'"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              <div>{{ tl('This name is not available.') }}</div>
              <div v-if="nameCheck.companyExisted" class="text-body-2 mt-1">{{ tl('A company with this name already exists.') }}</div>
              <ul v-if="Object.keys(nameCheck.conflicts || {}).length" class="text-body-2 mt-1 mb-0">
                <li v-for="(rows, key) in nameCheck.conflicts" :key="key">
                  {{ key }}: {{ rows.length }} {{ tl('match(es)') }}
                </li>
              </ul>
            </v-alert>
            <v-alert
              v-else-if="nameCheck.status === 'error'"
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              {{ nameCheck.message || tl('Could not check availability. Please try again.') }}
            </v-alert>

            <!--
              T-Link 에서 넘어온 값은 **아직 화면에 안 보인다** — 회사명 칸은 신청종류까지
              골라야 나타나고, 업종·프로모터는 이름 조회 게이트 뒤에 있다. 아무 표시가 없으면
              "전달이 안 됐다" 고 오해하게 되므로(실제 문의), 무엇이 넘어왔는지 먼저 알려 준다.
            -->
            <v-alert
              v-if="!readOnly && carriedOverSummary.length"
              type="success"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              <div class="font-weight-medium">
                {{ tl('Carried over from the linked T-Link') }}: {{ carriedOverSummary.join(' · ') }}
              </div>
              <div class="text-body-2 mt-1" style="opacity:.85">
                {{ tl('Choose the company details below — the proposed name is already filled in.') }}
              </div>
            </v-alert>

            <v-alert
              v-if="!nameGateOpen"
              type="info"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              {{ tl('The remaining sections open once the company name passes the availability check.') }}
            </v-alert>
          </div>

          <!-- Dynamic-list (promoters) rendered directly with DynamicListItem -->
          <div v-else-if="section.type === 'dynamic-list'" class="pt-4">
            <v-alert
              v-if="formData.promoters.length === 0"
              type="warning"
              variant="tonal"
              class="mb-4"
            >
              <v-icon class="mr-2">mdi-alert</v-icon>
              {{ tl('At least one promoter is required') }}
            </v-alert>

            <DynamicListItem
              v-for="(promoter, index) in formData.promoters"
              :key="index"
              :index="index"
              :item-data="promoter"
              :item-label="section.itemLabel || 'Item'"
              :border-color="section.borderColor"
              :theme-color="section.themeColor"
              :readonly="props.readOnly"
              :ref-data="dynamicRefData"
              density="comfortable"
              :header-fields="promoterHeaderFields(promoter)"
              :type-selector="section.itemTemplate?.typeSelector || null"
              :variants="section.itemTemplate?.variants || null"
              :contacts="contacts"
              :contacts-loading="isLoadingContacts"
              :contact-placeholder="getContactPlaceholder(promoter)"
              :contact-hint="getContactHint(promoter)"
              @update:item="updatePromoter(index, $event)"
              @remove="removePromoter(index)"
              @contact-change="(val) => onPromoterNameChange(index, val)"
              @date-picker-change="(val) => onDatePickerChange(index, val)"
              @date-blur="() => formatDateInput(index)"
            />
          </div>

          <!-- Clarification 6) Business Code + 7) Other Comments —
               CRS 는 이 둘도 Clarifications 섹션 안에 있다. 렌더러가 섹션 단위라
               설정에서만 분리하고 화면에서는 같은 패널에 이어 붙인다. -->
          <template v-if="section.id === 'clarification'">
            <div v-if="businessCodeSection" class="mt-4">
              <div class="text-subtitle-2 font-weight-medium mb-1">
                {{ tl(businessCodeSection.title) }} <span class="text-error">*</span>
              </div>
              <SectionRenderer
                ref="bcSectionRef"
                :section="businessCodeSection"
                :form-data="formData"
                :readonly="props.readOnly"
                :ref-data="dynamicRefData"
                density="comfortable"
                @update:list="onBusinessCodesUpdate"
              />
            </div>
            <div v-if="otherCommentsSection" class="mt-4">
              <SectionRenderer
                :section="otherCommentsSection"
                :form-data="formData"
                :readonly="props.readOnly"
                :ref-data="dynamicRefData"
                density="comfortable"
                @update:field="handleFieldUpdate"
              />
            </div>
          </template>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Required Fields Notice -->
    <v-alert type="info" variant="tonal" density="compact" class="mt-4">
      <span class="text-error font-weight-bold">*</span> {{ tl('indicates required fields') }}
    </v-alert>
  </v-form>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch, onMounted, nextTick } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { apiClient } from '@/shared/api/client';
import type { ClarificationWithFile, PromoterInfo } from '../../api/reserveNameApi';
import SectionRenderer from '../shared/renderers/SectionRenderer.vue';
import DynamicListItem from '../shared/renderers/DynamicListItem.vue';
import { useRnFormConfig } from './composables/useRnFormConfig';
import { requiresIdExpiryDate, requiresSecondaryId } from '../shared/crsIdentityRules';
import { useCredentialManagement } from '../../composables/useCredentialManagement';

// Translation
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

// Form config from JSON
const { sections, idTypeMapping, refData } = useRnFormConfig();

// Contact interface (common.js /officers response)
interface Contact {
  contactCd: string;
  contactName: string;
  contactRegNo: string;
  clientCd: string;
  categoryCd: string;
  displayName: string;
  contactEmail: string;
  dateOfBirth?: string | null;
  idTypeCd?: string;
  idTypeName?: string;
}

// PromoterInfo extended (UI-only fields)
interface PromoterInfoExtended extends PromoterInfo {
  dateMenu?: boolean;
  datePickerValue?: Date | null;
}

/**
 * 새 프로모터 한 건. CRS 는 Promoter Type 만 기본값(INDIVIDUAL)을 주고
 * Registered with SSM 은 비워 둔 채 사용자가 고르게 한다 — 같은 기준으로 만든다.
 */
function blankPromoter(): PromoterInfoExtended {
  return {
    promoterType: 'INDIVIDUAL',
    promoterName: '',
    identificationType: 'NRIC',
    identificationNumber: '',
    idExpiryDate: '',
    secondaryIdType: '',
    secondaryIdNumber: '',
    secondaryIdExpiryDate: '',
    salutation: '',
    dateOfBirth: '',
    email: '',
    isSsmRegistered: '',
    compRegType: '',
    compRegNoFormat: '',
    compRegNo: '',
    memberCategory: '',
    memberSubCategory: '',
    entityNo: '',
    dateMenu: false,
    datePickerValue: null,
  };
}

/** 개인/법인에 따라 이름 칸 라벨이 달라진다(CRS: Individual Name / Entity Name). */
const PROMOTER_NAME_LABEL: Record<string, string> = {
  INDIVIDUAL: 'Individual Name',
  BODY_CORPORATE: 'Entity Name',
};

// Props
interface Props {
  taskConfig?: any;
  loading?: boolean;
  taskId?: string | number | null;
  readOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  taskConfig: null,
  loading: false,
  taskId: null,
  readOnly: false,
});

// Emits
const emit = defineEmits<{
  (e: 'update:formData', data: any): void;
  (e: 'update:valid', valid: boolean): void;
}>();

// Refs
const formRef = ref<any>(null);
const formValid = ref(false);

// Contact list
const contacts = ref<Contact[]>([]);
const isLoadingContacts = ref(false);
const isSelectingContact = ref<Set<number>>(new Set());

// Contact loading
const loadContacts = async () => {
  isLoadingContacts.value = true;
  try {
    const response = await apiClient.get('/startTask/common/officers');
    const data = (response as any)?.data || response;
    contacts.value = data?.officers || [];
  } catch (error) {
    console.error('[ReserveNameForm] Failed to load contacts:', error);
    contacts.value = [];
  } finally {
    isLoadingContacts.value = false;
  }
};

// CRS Name Search 코드테이블 — 단일 소스는 backend/data/malaysia/nameSearchMeta.json.
// 프론트는 이 배치 API(GET /marketplace/v1/malaysia/entity/name-search-meta) 결과만 사용하고
// 로컬 사본을 두지 않는다(재캡처 시 백엔드 파일 1개만 갱신하면 되도록).
const nsMeta = ref<any>(null);
const isMetaLoading = ref(true);
const metaLoadError = ref('');
const loadNameSearchMeta = async () => {
  isMetaLoading.value = true;
  metaLoadError.value = '';
  try {
    // apiClient.get()은 이미 axios response.data(파싱된 바디 {success, data})를 반환한다 — 여기서 또 .data를
    // 벗겨내면 안 된다(벗기면 meta 자체를 다시 언랩하려다 success 필드를 못 찾고 실패함).
    const body = await apiClient.get<any>('/marketplace/v1/malaysia/entity/name-search-meta');
    if (body?.success && body?.data) {
      nsMeta.value = body.data;
    } else {
      metaLoadError.value = (body as any)?.error || 'Failed to load Name Search options';
    }
  } catch (error: any) {
    console.error('[ReserveNameForm] name-search-meta API 실패', error);
    metaLoadError.value = error?.message || 'Failed to load Name Search options';
  } finally {
    isMetaLoading.value = false;
  }
};

// CRS(idpro) credential — Check Availability 호출용
const credentialMgmt = useCredentialManagement({ credentialType: 'idpro' });

// Determine promoter type from contact
const determinePromoterType = (contact: Contact): 'INDIVIDUAL' | 'BODY_CORPORATE' => {
  if (contact.categoryCd === 'B09002' || contact.displayName) {
    return 'INDIVIDUAL';
  }
  if (contact.contactRegNo && /^[0-9]{6,}[A-Z]?$/.test(contact.contactRegNo)) {
    if (contact.contactRegNo.length >= 6 && contact.contactRegNo.length <= 10) {
      return 'BODY_CORPORATE';
    }
  }
  return contact.displayName ? 'INDIVIDUAL' : 'BODY_CORPORATE';
};

// ID type mapping helpers
const mapDisplayNameToIdType = (displayName: string): string => {
  const nameUpper = displayName.toUpperCase();
  for (const [keyword, value] of Object.entries(idTypeMapping.byDisplayName)) {
    if (nameUpper.includes(keyword)) return value;
  }
  return 'NRIC';
};

const mapIdTypeNameToValue = (idTypeName: string): string => {
  if (!idTypeName) return '';
  const nameUpper = idTypeName.toUpperCase().trim();
  if (idTypeMapping.byName[nameUpper]) return idTypeMapping.byName[nameUpper];
  for (const [keyword, value] of Object.entries(idTypeMapping.byDisplayName)) {
    if (nameUpper.includes(keyword)) return value;
  }
  return '';
};

const mapIdTypeCdToValue = (idTypeCd: string): string => {
  if (!idTypeCd) return '';
  return idTypeMapping.byCode[idTypeCd.toUpperCase().trim()] || '';
};

// Contact placeholder/hint helpers
function getContactPlaceholder(promoter: any): string {
  return promoter.promoterType === 'BODY_CORPORATE'
    ? tl('Search contact or enter company name...')
    : tl('Search contact or enter name...');
}

function getContactHint(promoter: any): string {
  return promoter.promoterType === 'BODY_CORPORATE'
    ? tl('Search contact or enter full company/agency name')
    : tl('Search contact or enter full name according to identification type');
}

// Promoter Name change (contact auto-fill)
const onPromoterNameChange = (index: number, value: any) => {
  let contactName: string | null = null;
  let contact: Contact | null = null;

  if (!value) return;

  if (typeof value === 'object' && value !== null) {
    contact = value as Contact;
    contactName = contact.contactName || '';
  } else if (typeof value === 'string') {
    contactName = value.trim();
    contact = contacts.value.find(c => c.contactName === contactName) || null;
  } else {
    return;
  }

  if (contact) {
    isSelectingContact.value.add(index);
    try {
      const promoter = formData.promoters[index];
      promoter.promoterName = contact.contactName.toUpperCase();

      const determinedPromoterType = determinePromoterType(contact);
      promoter.promoterType = determinedPromoterType;

      if (contact.contactRegNo) {
        promoter.identificationNumber = contact.contactRegNo.toUpperCase();
      }

      if (determinedPromoterType === 'INDIVIDUAL') {
        let mappedIdType = '';
        if (contact.idTypeName) {
          mappedIdType = mapIdTypeNameToValue(contact.idTypeName);
        } else if (contact.idTypeCd) {
          mappedIdType = mapIdTypeCdToValue(contact.idTypeCd);
        } else if (contact.displayName) {
          mappedIdType = mapDisplayNameToIdType(contact.displayName);
        }
        // 화면 셀렉트는 CRS 1차 신분증 5종(refData.idTypes)만 보여 준다. 연락처에서 따온 값이
        // 그 목록에 없으면(예: Police ID — CRS 에선 2차 전용) 셀렉트가 빈칸이 되므로 기본값으로 되돌린다.
        const isValidIdType = (refData.idTypes || []).some(opt => opt.value === mappedIdType);
        promoter.identificationType = isValidIdType ? mappedIdType : 'NRIC';
      } else if (determinedPromoterType === 'BODY_CORPORATE') {
        promoter.identificationType = '';
      }

      if (contact.contactEmail) {
        promoter.email = contact.contactEmail;
      }

      if (determinedPromoterType === 'INDIVIDUAL' && contact.dateOfBirth) {
        try {
          const birthDate = new Date(contact.dateOfBirth);
          if (!isNaN(birthDate.getTime())) {
            // LDateField 는 저장을 ISO 로 고정한다(표시는 사용자 포맷). 여기서도 ISO 로 넣는다.
            // ⚠️ toISOString() 을 쓰면 안 된다 — UTC 로 바꾸면서 **날짜가 하루 밀린다**.
            //   서버는 "1961-11-27 00:00:00.0" 처럼 시간대 없는 문자열을 주고 브라우저는 이를
            //   로컬 자정으로 읽는데, 한국(UTC+9)에서 UTC 로 돌리면 1961-11-26 15:00 → 26일이 된다.
            //   (실측: IC 611127 인 연락처가 1961-11-26 으로 채워졌다.) 로컬 날짜 성분을 그대로 쓴다.
            promoter.dateOfBirth = toIsoDateLocal(birthDate);
            (promoter as PromoterInfoExtended).datePickerValue = birthDate;
          }
        } catch (error) {
          console.warn('[ReserveNameForm] Failed to parse dateOfBirth:', contact.dateOfBirth, error);
        }
      }
    } finally {
      nextTick(() => {
        isSelectingContact.value.delete(index);
      });
    }
  } else {
    if (contactName) {
      formData.promoters[index].promoterName = contactName.toUpperCase();
    }
  }
};

// Mount
onMounted(async () => {
  await nextTick();
  window.scrollTo({ top: 0, behavior: 'smooth' });
  await loadContacts();
  await loadNameSearchMeta();
  await credentialMgmt.loadCredentials();
});

// Expansion panels
const expandedPanels = ref(props.readOnly ? [] : ['details', 'clarification', 'promoters']);

// Watch formValid
watch(formValid, (newVal) => {
  emit('update:valid', newVal && formData.promoters.length > 0);
});

// Form Data — CRS 실화면과 동일하게 category는 기본값 없이 사용자가 직접 선택해야 함(Please select).
const formData = reactive({
  entityName: '',
  purpose: '',
  category: '',
  companyType: '',
  subType: '',
  suffix: '',
  typeOfApplication: '',
  clarification01: '',
  clarification02: '',
  clarification03: { text: '' } as ClarificationWithFile,
  clarification04: { text: '' } as ClarificationWithFile,
  clarification05: { text: '' } as ClarificationWithFile,
  // 6) Business Code — CRS 는 MSIC 선택표(최대 3). 자유 입력이 아니다.
  businessCodes: [''] as string[],
  // 7) Other Comments — 예전 라벨은 "Business Nature" 였는데 CRS 에 그런 칸은 없다.
  clarification07: '',
  promoters: [blankPromoter()] as PromoterInfoExtended[],
});

// Watch formData
watch(formData, (newVal) => {
  emit('update:formData', { ...newVal });
}, { deep: true });

// CRS Name Search cascade — 백엔드 name-search-meta API 응답(nsMeta) 소비. 2026-08-19 실화면 재검증 기준:
//  - category는 기본값 없음(Please select), 선택 전엔 purpose가 안내 placeholder 텍스트만 표시
//  - Suffix는 LOCAL(34125)에만 존재 (FOREIGN엔 필드 자체가 없음)
//  - Sub Type은 항상 노출되지만 FOREIGN에선 CRS 화면상 필수(*)가 아님(subTypeRequiredByCategory)
//  - Proposed Company Name은 typeOfApplication=PROPOSED COMPANY NAME(34108)일 때만 노출
//    (GUARANTEED INCORPORATION NAME 선택 시 시스템이 자동 생성 — 입력 불필요)
const M = computed<any>(() => nsMeta.value || {});
const isLocalCat = computed(() => formData.category === (M.value.suffixOnlyForCategory || '34125'));
const categoryOptions = computed(() => M.value.category || []);
const purposeOptions = computed(() => {
  const entry = M.value.purposeByCategory?.[formData.category];
  if (entry) return [entry];
  return [{ value: '', label: M.value.purposePlaceholderBeforeCategory || 'N - NEW INCORPORATION / REGISTRATION' }];
});
const typeOfCompanyOptions = computed(() => M.value.typeOfCompanyByCategory?.[formData.category] || []);
/**
 * Suffix 는 **Type of Company + Sub Type 조합**으로 결정된다.
 *   SHARES+PRIVATE → SDN. BHD./SENDIRIAN BERHAD, SHARES+PUBLIC → BHD./BERHAD,
 *   UNLIMITED → subType 무관 SDN./SENDIRIAN, GUARANTEE → PUBLIC 고정이라 BHD./BERHAD.
 * meta 는 2026-08-20 에 `suffixByTypeAndSubType` 로 정정됐는데 화면이 예전 키
 * (`suffixByTypeOfCompany`)를 계속 읽고 있었다 — 그 키는 meta 에 없어서 Suffix 목록이
 * **항상 비어 있었다**(2026-09-28 CRS 실화면 대조로 확인).
 */
const suffixOptions = computed(() => {
  if (!isLocalCat.value) return [];
  const byType = M.value.suffixByTypeAndSubType?.[formData.companyType];
  if (!byType) return [];
  // subType 을 아직 안 골랐으면 그 타입의 모든 후보를 중복 없이 보여 준다(UNLIMITED 처럼 동일한 경우 포함).
  if (!formData.subType) {
    const seen = new Set<string>();
    return Object.values(byType)
      .flat()
      .filter((o: any) => {
        if (!o || seen.has(o.value)) return false;
        seen.add(o.value);
        return true;
      });
  }
  return byType[formData.subType] || [];
});
const subTypeRequired = computed(() => !!M.value.subTypeRequiredByCategory?.[formData.category]);
const subTypeOptions = computed(() => {
  const locked = M.value.lock?.subTypeByTypeOfCompany?.[formData.companyType];
  const all = M.value.subType || [];
  return locked ? all.filter((o: any) => o.value === locked) : all;
});
/**
 * 이름 예약에서 쓸 수 있는 신청 종류는 **PROPOSED COMPANY NAME 하나뿐**이다.
 *
 * 코드표에는 `GUARANTEED INCORPORATION NAME`(34109)도 들어 있지만 그건 이름 예약이 아니다 —
 * 매뉴얼(NAME SEARCH §3.4): 시스템이 이름을 만들어 주고(`GI202606000001 SDN. BHD.` 형식)
 * 조회 없이 곧바로 **설립 폼**(Company Details → Directors → … → Payment)으로 넘어간다.
 * 그래서 이 화면에서 고르면 조회할 상호가 없어 name-search 가 400 을 돌려주고,
 * 게이트가 안 열려 하단 섹션이 통째로 막히는 **막다른 길**이 된다(실측).
 */
const NR_TYPE_OF_APPLICATION = '34108'; // PROPOSED COMPANY NAME
const typeOfApplicationOptions = computed(() => {
  const all = (M.value.typeOfApplication || [])
    .filter((o: any) => String(o.value) === NR_TYPE_OF_APPLICATION);
  if (!isLocalCat.value) {
    const lockedForeign = M.value.lock?.typeOfApplicationForeign;
    return lockedForeign ? all.filter((o: any) => o.value === lockedForeign) : all;
  }
  const locked = M.value.lock?.typeOfApplicationByTypeOfCompany?.[formData.companyType];
  return locked ? all.filter((o: any) => o.value === locked) : all;
});

// clarification 패널 안에 끼워 넣는 섹션은 패널 목록에서 뺀다.
const INLINE_SECTION_IDS = ['businessCodes', 'clarificationOther'];
/**
 * Sub Type 의 필수 여부는 **카테고리에 달려 있다**(CRS: LOCAL 만 필수, FOREIGN 은 아님).
 * 설정 JSON 은 정적이라 `required: true` 로 고정돼 있었고, 그래서 외국회사는 Sub Type 을
 * 고르지 않으면 Vuetify 규칙에 걸려 **제출 자체가 막혔다**(2026-09-29 실측).
 * meta 의 `subTypeRequiredByCategory` 를 따라 이 필드만 갈아 끼운다.
 */
const panelSections = computed(() => sections
  .filter((s: any) => !INLINE_SECTION_IDS.includes(s.id))
  .map((s: any) => {
    if (s.id !== 'details') return s;
    return {
      ...s,
      fields: s.fields.map((f: any) => (
        f.id === 'subType' ? { ...f, required: subTypeRequired.value } : f
      )),
    };
  }));
const findSection = (id: string) => (sections as any[]).find((s) => s.id === id) || null;
const businessCodeSection = computed(() => findSection('businessCodes'));
const otherCommentsSection = computed(() => findSection('clarificationOther'));
const promotersSection = computed(() => findSection('promoters'));

/** 헤더 이름 칸은 설정상 하나뿐이라, 프로모터 종류에 맞춰 라벨만 갈아 끼운다. */
function promoterHeaderFields(promoter: any) {
  const base = (promotersSection.value?.itemTemplate?.headerFields || []) as any[];
  return base.map((f) =>
    f.id === 'promoterName'
      ? { ...f, label: PROMOTER_NAME_LABEL[promoter?.promoterType] || f.label }
      : f,
  );
}

const bcSectionRef = ref<any>(null);
const onBusinessCodesUpdate = (payload: { sectionId: string; items: any[] }) => {
  formData.businessCodes = [...payload.items];
};

const dynamicRefData = computed(() => ({
  ...refData,
  categoryOptions: categoryOptions.value,
  purposeOptions: purposeOptions.value,
  typeOfCompanyOptions: typeOfCompanyOptions.value,
  suffixOptions: suffixOptions.value,
  subTypeOptions: subTypeOptions.value,
  typeOfApplicationOptions: typeOfApplicationOptions.value,
}));

// Visibility
const showEntityName = computed(() => formData.typeOfApplication === (M.value.proposedCompanyNameVisibleWhenTypeOfApplication || '34108'));

// ── Check Availability (CRS singleSimilarSearch 프록시) ──────────────
const nameCheck = reactive<{
  status: 'idle' | 'checking' | 'available' | 'unavailable' | 'error';
  message: string;
  companyExisted: boolean;
  conflicts: Record<string, any[]>;
}>({ status: 'idle', message: '', companyExisted: false, conflicts: {} });

const canCheckAvailability = computed(() => {
  if (!formData.category || !formData.companyType || !formData.purpose) return false;
  if (isLocalCat.value && !formData.suffix) return false;
  if (showEntityName.value && !formData.entityName.trim()) return false;
  return nameCheck.status !== 'checking';
});

/**
 * 이름 조회를 통과했는가. CRS 에 draft 를 이미 만든 태스크는 (재진입 시 조회 상태가 없어도)
 * 계속 열어 둔다 — 설립(DI) 화면과 같은 규칙이다.
 */
const crsSubmissionId = ref('');

/**
 * 이름 조회를 통과한 시점의 **이름 구성 전체**. 이것이 지금 값과 같으면 다시 조회하지 않아도 된다.
 * (접미사·회사종류가 바뀌면 전체 회사명이 달라지므로 다 포함한다.)
 */
const nameSignature = computed(() => [
  formData.category, formData.companyType, formData.subType, formData.suffix,
  formData.typeOfApplication, formData.entityName.trim().toUpperCase(),
].join('|'));
const verifiedSignature = ref('');

/**
 * CRS draft id 와 **그 draft 를 만들 때 통과한 이름 구성**을 알려 준다.
 *
 * 재진입하면 조회 상태가 초기화되므로, 이게 없으면 기존 draft 를 영영 갱신할 수 없었다.
 * ⚠️ 서명을 여기서 `nameSignature.value` 로 찍으면 안 된다 — 그건 **지금 폼에 있는 값**이라,
 *   이름을 바꿔 임시저장한 뒤 재진입하면 **조회한 적 없는 이름이 통과한 것으로** 둔갑한다.
 *   그래서 호출부가 저장해 둔 서명을 넘겨 주고, 없으면(옛 태스크) 다시 조회하게 둔다.
 */
const setCrsSubmissionId = (id: string, verifiedName?: string) => {
  crsSubmissionId.value = id || '';
  verifiedSignature.value = id ? String(verifiedName || '') : '';
};

/**
 * 저장해 둔 이름 조회 결과를 되살린다(임시저장 후 재진입).
 *
 * 서명은 **실제 조회에 통과했을 때만** 저장되므로(호출부가 `isNameVerified()` 를 보고 넣는다),
 * 그 서명이 지금 이름 구성과 같으면 다시 조회할 이유가 없다. 화면에도 조회 결과를
 * 그대로 띄워 준다 — 사용자가 "인증 기록이 사라졌다" 고 느끼지 않도록.
 */
const restoreVerifiedName = (verifiedName?: string) => {
  const sig = String(verifiedName || '');
  if (!sig) return;
  verifiedSignature.value = sig;
  if (sig === nameSignature.value && nameCheck.status === 'idle') {
    nameCheck.status = 'available';
  }
};

/** 지금 폼의 이름 구성 서명. 제출 성공 시 호출부가 draft 와 함께 저장해 둔다. */
const getNameSignature = () => nameSignature.value;

/**
 * 이름이 확인됐는가 — **서명 하나로만** 판정한다.
 *
 * 조회에 통과하면 `checkAvailability` 가 그때의 서명을 넣고, 임시저장·draft 재진입도
 * 저장된 서명을 되살린다. 그래서 "지금 이름 구성 == 통과했던 이름 구성" 이 곧 판정이다.
 * `nameCheck.status === 'available'` 을 같이 보면 안 된다 — 되살린 상태는 **그때 그 이름**에
 * 대한 기록이라, 이름을 고친 뒤에도 통과로 읽혀 버린다(실측으로 걸렸다).
 */
const nameVerified = computed(() =>
  !!verifiedSignature.value && verifiedSignature.value === nameSignature.value);
const isNameVerified = () => nameVerified.value;

/** 한 번 열린 게이트는 닫지 않는다 — 이름을 고치는 중에 입력 칸이 사라지지 않도록. */
const gateEverOpen = ref(false);
/**
 * T-Link 프리필로 실제 들어온 것들의 요약. 게이트가 닫혀 있는 동안에도 보여 준다.
 * (회사명 칸·업종·프로모터가 전부 숨어 있어 사용자가 확인할 방법이 없기 때문이다.)
 */
const carriedOverSummary = computed(() => {
  const out: string[] = [];
  const nm = String(formData.entityName || '').trim();
  if (nm) out.push(`${tl('Proposed name')}: ${nm}`);
  const bc = formData.businessCodes.filter(Boolean).length;
  if (bc) out.push(`${tl('Business code(s)')}: ${bc}`);
  const pr = formData.promoters.filter((p) => String(p.promoterName || '').trim()).length;
  if (pr) out.push(`${tl('Promoter(s)')}: ${pr}`);
  return out;
});

const nameGateOpen = computed(() =>
  // 읽기 전용(완료된 태스크)은 무엇을 신고했는지 그대로 보여 줘야 한다 — 게이트를 걸지 않는다.
  props.readOnly || gateEverOpen.value || nameVerified.value || !!crsSubmissionId.value);
watch(nameGateOpen, (open) => { if (open) gateEverOpen.value = true; }, { immediate: true });

// 게이트가 열리면 아래 패널을 펼쳐 준다(닫혀 있으면 사용자가 찾아 열어야 한다).
watch(nameGateOpen, (open) => {
  if (!open || props.readOnly) return;
  for (const id of ['clarification', 'promoters']) {
    if (!expandedPanels.value.includes(id)) expandedPanels.value.push(id);
  }
});

const resetNameCheck = () => {
  if (nameCheck.status === 'idle') return;
  nameCheck.status = 'idle';
  nameCheck.message = '';
  nameCheck.companyExisted = false;
  nameCheck.conflicts = {};
};

const checkAvailability = async () => {
  nameCheck.status = 'checking';
  nameCheck.message = '';
  try {
    const body = await apiClient.post<{
      available: boolean; company_existed: boolean; conflicts: Record<string, any[]>;
    }>('/marketplace/v1/malaysia/entity/name-search-check', {
      proposed_name: formData.entityName.trim(),
      purpose: formData.purpose,
      category: formData.category,
      type_of_company: formData.companyType,
      sub_type: formData.subType || undefined,
      suffix: formData.suffix || undefined,
      type_of_application: formData.typeOfApplication || undefined,
      crs_credential_id: credentialMgmt.selectedCredentialId.value || undefined,
    });
    if (!body?.success || !body?.data) {
      nameCheck.status = 'error';
      nameCheck.message = (body as any)?.error || tl('Could not check availability. Please try again.');
      return;
    }
    nameCheck.companyExisted = !!body.data.company_existed;
    nameCheck.conflicts = body.data.conflicts || {};
    nameCheck.status = body.data.available ? 'available' : 'unavailable';
    if (nameCheck.status === 'available') verifiedSignature.value = nameSignature.value;
  } catch (error: any) {
    nameCheck.status = 'error';
    nameCheck.message = error?.message || tl('Could not check availability. Please try again.');
  }
};

// Field update handler from SectionRenderer
const handleFieldUpdate = (payload: { sectionId: string; fieldId: string; value: any }) => {
  const { fieldId, value } = payload;

  // Handle file fields
  if (fieldId.startsWith('_file.')) {
    const realFieldId = fieldId.replace('_file.', '');
    onFileChange(realFieldId, value);
    return;
  }

  // Handle clarification fields with file data
  if (['clarification03', 'clarification04', 'clarification05'].includes(fieldId)) {
    (formData as any)[fieldId].text = value;
    return;
  }

  // Special handlers (CRS cascade)
  if (fieldId === 'category') {
    formData.category = value;
    onCategoryChange();
    resetNameCheck();
    return;
  }
  if (fieldId === 'companyType') {
    formData.companyType = value;
    onCompanyTypeChange();
    resetNameCheck();
    return;
  }
  // Sub Type 도 Suffix 후보를 바꾼다(SHARES+PRIVATE → SDN. BHD. / SHARES+PUBLIC → BHD.).
  //   예전에는 companyType 만 Suffix 를 재계산해서, Sub Type 을 바꿔도 이전 접미사가 남았다.
  if (fieldId === 'subType') {
    formData.subType = value;
    onSubTypeChange();
    resetNameCheck();
    return;
  }

  // Fields that invalidate a previous availability check
  if (['entityName', 'subType', 'suffix', 'typeOfApplication'].includes(fieldId)) {
    resetNameCheck();
  }

  // Standard field update
  if (fieldId in formData) {
    (formData as any)[fieldId] = value;
  }
};

// Promoter update from DynamicListItem
function updatePromoter(index: number, data: Record<string, any>) {
  const oldPromoter = formData.promoters[index];
  const newPromoter = { ...data } as PromoterInfoExtended;

  // Detect promoterType change
  if (oldPromoter.promoterType !== newPromoter.promoterType) {
    onPromoterTypeChange(index, newPromoter);
  }

  // 신분증 종류가 바뀌면 번호와 **그 종류에만 딸린 칸**(만료일·2차 신분증)을 비운다.
  // 안 비우면 MYKAS→MYKAD 로 바꿨을 때 화면에서 사라진 만료일이 값만 남아 그대로 제출된다.
  if (oldPromoter.identificationType !== newPromoter.identificationType &&
      !isSelectingContact.value.has(index)) {
    newPromoter.identificationNumber = '';
    if (!requiresIdExpiryDate(newPromoter.identificationType)) newPromoter.idExpiryDate = '';
    if (!requiresSecondaryId(newPromoter.identificationType)) {
      newPromoter.secondaryIdType = '';
      newPromoter.secondaryIdNumber = '';
      newPromoter.secondaryIdExpiryDate = '';
    }
  }

  // SSM 등록 여부가 바뀌면 반대쪽 분기 값을 비운다(같은 이유).
  //   email 도 같이 비운다 — CRS 모달의 onRegisterWithSsmChanged 가 `email: null` 로 지운다.
  //   안 지우면 미등록 분기에서 입력한 이메일이 화면에서 사라진 채 등록 분기로 실려 나간다.
  if (oldPromoter.isSsmRegistered !== newPromoter.isSsmRegistered) {
    newPromoter.email = '';
    if (newPromoter.isSsmRegistered === 'true') {
      newPromoter.memberCategory = '';
      newPromoter.memberSubCategory = '';
      newPromoter.entityNo = '';
    } else {
      newPromoter.compRegType = '';
      newPromoter.compRegNoFormat = '';
      newPromoter.compRegNo = '';
    }
  }

  // 주정부가 아니면 하위 분류는 존재하지 않는다.
  if (newPromoter.memberCategory !== 'STATE_GOVERNMENT') newPromoter.memberSubCategory = '';

  formData.promoters[index] = newPromoter;
}

// Business Logic — CRS cascade 재현 (2026-08-19 실화면 검증 기준)
// Type of Company 변경: Suffix 리셋(LOCAL만 해당), Sub Type/Type of Application은 옵션이 1개로
// 좁혀지면(GUARANTEE) 그 값으로 자동 고정, 아니면 현재값 유지(옵션에 없으면 초기화).
const onCompanyTypeChange = () => {
  const sfx = suffixOptions.value;
  if (isLocalCat.value) {
    if (!sfx.some((o: any) => o.value === formData.suffix)) formData.suffix = '';
  } else {
    formData.suffix = '';
  }
  const st = subTypeOptions.value;
  if (st.length === 1) formData.subType = st[0].value;
  else if (!st.some((o: any) => o.value === formData.subType)) formData.subType = '';
  const toa = typeOfApplicationOptions.value;
  if (toa.length === 1) formData.typeOfApplication = toa[0].value;
  else if (!toa.some((o: any) => o.value === formData.typeOfApplication)) formData.typeOfApplication = '';
};

/** Sub Type 변경: 그 조합에 없는 Suffix 는 버린다(후보가 1개면 자동 선택). */
const onSubTypeChange = () => {
  if (!isLocalCat.value) {
    formData.suffix = '';
    return;
  }
  const sfx = suffixOptions.value;
  if (sfx.length === 1) formData.suffix = (sfx[0] as any).value;
  else if (!sfx.some((o: any) => o.value === formData.suffix)) formData.suffix = '';
};

// Category 변경: Purpose 자동 도출(LOCAL=N-NEW INCORPORATION/FOREIGN=N-NEW REGISTRATION),
// Type of Company 옵션 전체 교체 → 리셋 후 하위(Suffix/SubType/TypeOfApplication) 재계산.
const onCategoryChange = () => {
  formData.purpose = (M.value.purposeByCategory?.[formData.category] || {}).value || '';
  const toc = typeOfCompanyOptions.value;
  if (!toc.some((o: any) => o.value === formData.companyType)) formData.companyType = '';
  onCompanyTypeChange();
};

/**
 * 프로모터 인원 제한. **최대는 항상 5**, 최소는 조합에 따라 1 또는 2 다.
 * CRS 번들(roc-registration/219.*.js) `populatePromoterNo()` 를 그대로 옮겼다:
 *
 *   FOREIGN                              → 1
 *   LOCAL + LIMITED BY SHARES + PUBLIC   → 2
 *   LOCAL + LIMITED BY SHARES + PRIVATE  → 1
 *   LOCAL + LIMITED BY GUARANTEE         → 2
 *   LOCAL + UNLIMITED                    → 1
 *
 * (공개회사·보증유한회사는 발기인이 2인 이상이어야 한다는 CA 2016 요건과 같다.)
 */
const MAX_PROMOTERS = 5;
const CRS_CODE = {
  CATEGORY_FOREIGN: '34124',
  TYPE_SHARES: '34101',
  TYPE_UNLIMITED: '34102',
  TYPE_GUARANTEE: '34103',
  SUB_PUBLIC: '33940',
} as const;
const promoterMin = computed(() => {
  if (formData.category === CRS_CODE.CATEGORY_FOREIGN) return 1;
  if (formData.companyType === CRS_CODE.TYPE_GUARANTEE) return 2;
  if (formData.companyType === CRS_CODE.TYPE_SHARES) {
    return formData.subType === CRS_CODE.SUB_PUBLIC ? 2 : 1;
  }
  return 1; // UNLIMITED, 그리고 아직 안 고른 상태
});

const addPromoter = () => {
  if (formData.promoters.length >= MAX_PROMOTERS) return;
  formData.promoters.push(blankPromoter());
  if (!expandedPanels.value.includes('promoters')) {
    expandedPanels.value.push('promoters');
  }
};

// Date format: DD MMMM YYYY (e.g., 18 FEBRUARY 2026)
const MONTH_NAMES = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

/** Date → `YYYY-MM-DD`. **로컬 날짜 성분**으로 만든다(UTC 변환 금지 — 날짜가 밀린다). */
function toIsoDateLocal(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

function formatDateToDisplay(date: Date): string {
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

function parseDateString(value: string): Date | null {
  // Already in DD MMMM YYYY format
  const displayMatch = value.match(/^(\d{1,2})\s+([A-Z]+)\s+(\d{4})$/);
  if (displayMatch) {
    const monthIdx = MONTH_NAMES.indexOf(displayMatch[2]);
    if (monthIdx >= 0) {
      return new Date(parseInt(displayMatch[3]), monthIdx, parseInt(displayMatch[1]));
    }
  }
  // ISO format: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  }
  // DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [d, m, y] = value.split('/');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  }
  // DDMMYYYY
  if (/^\d{8}$/.test(value)) {
    return new Date(parseInt(value.substring(4, 8)), parseInt(value.substring(2, 4)) - 1, parseInt(value.substring(0, 2)));
  }
  return null;
}

const onDatePickerChange = (index: number, value: any) => {
  if (value) {
    const date = new Date(value);
    formData.promoters[index].dateOfBirth = formatDateToDisplay(date);
  }
  (formData.promoters[index] as PromoterInfoExtended).dateMenu = false;
};

const formatDateInput = (index: number) => {
  const value = formData.promoters[index].dateOfBirth;
  if (!value) return;
  // Already in DD MMMM YYYY format
  if (/^\d{1,2}\s+[A-Z]+\s+\d{4}$/.test(value)) return;
  const date = parseDateString(value);
  if (date && !isNaN(date.getTime())) {
    formData.promoters[index].dateOfBirth = formatDateToDisplay(date);
  }
};

const removePromoter = (index: number) => {
  formData.promoters.splice(index, 1);
};

const onPromoterTypeChange = (index: number, promoter?: PromoterInfoExtended) => {
  const p = promoter || formData.promoters[index];
  if (p.promoterType === 'BODY_CORPORATE') {
    p.identificationType = '';
    p.identificationNumber = '';
    p.idExpiryDate = '';
    p.secondaryIdType = '';
    p.secondaryIdNumber = '';
    p.secondaryIdExpiryDate = '';
    p.salutation = '';
    p.dateOfBirth = '';
    p.datePickerValue = null;
  } else {
    p.identificationNumber = '';
    p.isSsmRegistered = '';
    p.compRegType = '';
    p.compRegNoFormat = '';
    p.compRegNo = '';
    p.memberCategory = '';
    p.memberSubCategory = '';
    p.entityNo = '';
    if (!p.identificationType) {
      p.identificationType = 'NRIC';
    }
  }
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const onFileChange = async (field: string, file: File | null) => {
  if (!file) {
    (formData as any)[field].fileName = undefined;
    (formData as any)[field].fileContentBase64 = undefined;
    (formData as any)[field].contentType = undefined;
    return;
  }

  const base64 = await fileToBase64(file);
  (formData as any)[field].fileName = file.name;
  (formData as any)[field].fileContentBase64 = base64;
  (formData as any)[field].contentType = file.type;
};

// Validation
const validationErrors = ref<string[]>([]);

const validate = async (): Promise<boolean> => {
  validationErrors.value = [];

  // Vuetify form validation (checks :rules on rendered fields)
  let formValidResult = true;
  const vuetifyErrors: string[] = [];
  if (formRef.value) {
    const result = await formRef.value.validate();
    formValidResult = result.valid;
    if (!result.valid && result.errors?.length > 0) {
      for (const err of result.errors) {
        for (const msg of err.errorMessages) {
          vuetifyErrors.push(msg);
        }
      }
    }
  }

  // Custom validation (business logic)
  if (!formData.category) {
    validationErrors.value.push(tl('Category is required'));
  }
  if (!formData.companyType) {
    validationErrors.value.push(tl('Company Type is required'));
  }
  if (subTypeRequired.value && !formData.subType) {
    validationErrors.value.push(tl('Company SubType is required'));
  }
  if (isLocalCat.value && !formData.suffix) {
    validationErrors.value.push(tl('Suffix is required'));
  }
  if (showEntityName.value && (!formData.entityName || formData.entityName.trim() === '')) {
    validationErrors.value.push(tl('Proposed Company Name is required'));
  }
  // CRS Clarification 6 — MSIC 최소 1건(최대 3건은 렌더러가 막는다).
  if (formData.businessCodes.filter(Boolean).length === 0) {
    validationErrors.value.push(tl('At least one Business Code is required'));
  }
  if (!isNameVerified()) {
    validationErrors.value.push(tl('Please check name availability before submitting'));
  }

  if (formData.promoters.length < promoterMin.value) {
    validationErrors.value.push(
      `${tl('At least')} ${promoterMin.value} ${tl('promoter(s) are required for this company type')}`,
    );
    if (!expandedPanels.value.includes('promoters')) {
      expandedPanels.value.push('promoters');
    }
  }
  if (formData.promoters.length > MAX_PROMOTERS) {
    validationErrors.value.push(
      `${tl('At most')} ${MAX_PROMOTERS} ${tl('promoters are allowed')}`,
    );
  }
  if (formData.promoters.length === 0) {
    // 아래 항목별 검사는 돌 것이 없다.
  } else {
    // CRS Add Promoter 모달의 필수항목을 분기까지 그대로 옮긴 것이다.
    //   개인: 신분증 종류/번호 + MYKAS 면 만료일 + 여권이면 2차 신분증 3칸 + 생년월일 + 이메일
    //   법인: SSM 등록여부 → YES 는 등록유형/등록번호, NO 는 멤버분류(+주정부면 하위분류)/이메일
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    formData.promoters.forEach((promoter, index) => {
      const who = `${tl('Promoter')} ${index + 1}`;
      const isIndividual = promoter.promoterType === 'INDIVIDUAL';
      const need = (value: any, label: string) => {
        if (!String(value ?? '').trim()) {
          validationErrors.value.push(`${who}: ${tl(label)} ${tl('is required')}`);
        }
      };
      const needEmail = () => {
        if (!String(promoter.email ?? '').trim()) {
          validationErrors.value.push(`${who}: ${tl('Email')} ${tl('is required')}`);
        } else if (!EMAIL_RE.test(String(promoter.email))) {
          validationErrors.value.push(`${who}: ${tl('Please enter a valid email address')}`);
        }
      };

      if (!promoter.promoterType) {
        need('', 'Promoter Type');
        return;
      }
      need(promoter.promoterName, isIndividual ? 'Individual Name' : 'Entity Name');

      if (isIndividual) {
        need(promoter.identificationType, 'Identification Type');
        need(promoter.identificationNumber, 'Identification Number');
        if (requiresIdExpiryDate(promoter.identificationType)) {
          need(promoter.idExpiryDate, 'Identification Expiry Date');
        }
        if (requiresSecondaryId(promoter.identificationType)) {
          need(promoter.secondaryIdType, 'Secondary Identification Type');
          need(promoter.secondaryIdNumber, 'Secondary Identification Number');
          need(promoter.secondaryIdExpiryDate, 'Secondary Identification Expiry Date');
        }
        need(promoter.dateOfBirth, 'Date of Birth');
        needEmail();
        return;
      }

      need(promoter.isSsmRegistered, 'Registered with SSM');
      if (promoter.isSsmRegistered === 'true') {
        need(promoter.compRegType, 'Type of Registration');
        need(promoter.compRegNo, 'Registration No.');
      } else if (promoter.isSsmRegistered === 'false') {
        need(promoter.memberCategory, 'Category of Member');
        if (promoter.memberCategory === 'STATE_GOVERNMENT') {
          need(promoter.memberSubCategory, 'Sub-Category of Member');
        }
        needEmail();
      }
    });
  }

  // If Vuetify found errors not caught by custom validation, include them
  if (!formValidResult && validationErrors.value.length === 0 && vuetifyErrors.length > 0) {
    const uniqueErrors = [...new Set(vuetifyErrors)];
    validationErrors.value.push(...uniqueErrors);
  }

  if (validationErrors.value.length > 0) {
    if (validationErrors.value.some(e => e.includes('Entity Name') || e.includes('Purpose') || e.includes('Company'))) {
      if (!expandedPanels.value.includes('details')) {
        expandedPanels.value.push('details');
      }
    }
    if (validationErrors.value.some(e => e.includes('Business Code'))) {
      if (!expandedPanels.value.includes('clarification')) {
        expandedPanels.value.push('clarification');
      }
    }
    return false;
  }

  return formValidResult;
};

const getValidationErrors = () => validationErrors.value;

// ── CRS 제출에 필요한 것들 ────────────────────────────────────────
//   자격증명은 Check Availability 와 **같은 것**을 쓴다(둘 다 CRS/idpro).
const getSelectedCredentialId = () => credentialMgmt.selectedCredentialId.value || null;

/** 자격증명이 아직 안 골라졌으면 목록을 읽어 첫 건을 쓴다. 끝내 없으면 false. */
const ensureCredentialSelected = async (): Promise<boolean> => {
  if (credentialMgmt.selectedCredentialId.value) return true;
  if (!credentialMgmt.credentials.value.length) await credentialMgmt.loadCredentials();
  if (credentialMgmt.selectedCredentialId.value) return true;
  // selectCredential 은 **자격증명 객체**를 받는다(id 를 넘기면 조용히 undefined 가 된다).
  const matching = credentialMgmt.mycoidCredentials.value || [];
  if (matching.length) credentialMgmt.selectCredential(matching[0]);
  return !!credentialMgmt.selectedCredentialId.value;
};

/** 이름 조회에서 걸린 충돌 목록. CRS payload 의 gazette/control/state 플래그가 여기서 나온다. */
const getNameCheckConflicts = () => ({ ...(nameCheck.conflicts || {}) });

/** 본문이나 첨부 중 하나라도 있으면 그대로, 둘 다 없으면 undefined. */
function clarificationOrUndefined(v: ClarificationWithFile | undefined) {
  if (!v) return undefined;
  return (v.text || v.fileName) ? v : undefined;
}

const getFormData = () => {
  return {
    entityName: formData.entityName.toUpperCase(),
    purpose: formData.purpose,
    category: formData.category,
    companyType: formData.companyType,
    suffix: formData.suffix || undefined,
    typeOfApplication: formData.typeOfApplication,
    subType: formData.subType || undefined,
    clarification01: formData.clarification01 || undefined,
    clarification02: formData.clarification02 || undefined,
    // 본문이 비어도 **첨부가 있으면** 넘긴다. 예전에는 text 만 봐서, 답을 적지 않고 파일만
    // 붙이면 첨부가 조용히 사라지고 "CRS 에서 직접 첨부하라"는 경고조차 뜨지 않았다.
    clarification03: clarificationOrUndefined(formData.clarification03),
    clarification04: clarificationOrUndefined(formData.clarification04),
    clarification05: clarificationOrUndefined(formData.clarification05),
    clarification07: formData.clarification07 || undefined,
    businessCodes: formData.businessCodes.filter(Boolean),
    promoters: formData.promoters,
  };
};

/** SectionRenderer(simple-list) 는 목록을 자기 안에 갖고 있어서 되돌릴 때 직접 넣어 줘야 한다. */
function bcInstance(): any {
  const raw = bcSectionRef.value;
  return Array.isArray(raw) ? raw[0] : raw;
}
function setBusinessCodeItems(items: string[]) {
  const inst = bcInstance();
  if (inst?.setListItems) inst.setListItems(items.length ? items : ['']);
}

/**
 * 업종 목록 렌더러가 **나중에 붙는 경우**를 메운다.
 *
 * Vuetify 확장패널은 펼치기 전까지 내용을 렌더하지 않는다. 읽기 전용(완료 태스크)은 패널이
 * 모두 접힌 채로 열리므로, 저장본을 읽는 시점(mount+100ms)에는 이 렌더러가 아직 없어
 * `setListItems` 가 조용히 무시됐다 → 나중에 펼치면 **업종이 빈 줄 하나**로 보였다
 * (모델에는 2건이 그대로 있는데 화면만 비어 있었다 — 2026-09-29 실측).
 * 렌더러가 생기는 순간 현재 값을 밀어 넣는다.
 */
function syncBusinessCodeWidget() {
  const inst = bcInstance();
  if (!inst?.setListItems) return;
  const want = formData.businessCodes.filter(Boolean);
  if (!want.length) return;
  const have = (inst.getListItems?.() || []).filter(Boolean);
  if (JSON.stringify(have) !== JSON.stringify(want)) setBusinessCodeItems(want);
}

/**
 * ★ `watch(bcSectionRef, …)` 로는 안 된다.
 *   이 ref 는 **배열**이고(템플릿 ref 가 배열로 잡힌다), Vue 는 마운트 때 같은 배열을
 *   **제자리 변경**하므로 ref 의 값 동일성이 바뀌지 않아 워처가 아예 발화하지 않는다.
 *   실측: 모델엔 업종 3건이 있는데 위젯은 `['']` 인 채로 남아, 화면엔 빈 줄이 보이고
 *   필드 필수 규칙("Business Code / Description is required")이 제출을 막았다.
 *
 *   그래서 **렌더러가 붙는 계기**(패널 펼침 / 이름 게이트 열림 / 모델 변경)를 보고
 *   nextTick 뒤에 밀어 넣는다. 한 번 더 늦게 붙는 경우까지 덮도록 짧게 재시도한다.
 */
watch(
  [expandedPanels, nameGateOpen, () => formData.businessCodes.join('|')],
  async () => {
    await nextTick();
    syncBusinessCodeWidget();
    setTimeout(syncBusinessCodeWidget, 120);
  },
  { immediate: true, deep: true },
);

const resetForm = () => {
  formData.entityName = '';
  formData.purpose = '';
  formData.category = '';
  formData.companyType = '';
  formData.subType = '';
  formData.suffix = '';
  formData.typeOfApplication = '';
  formData.clarification01 = '';
  formData.clarification02 = '';
  formData.clarification03 = { text: '' };
  formData.clarification04 = { text: '' };
  formData.clarification05 = { text: '' };
  formData.clarification07 = '';
  formData.businessCodes = [''];
  setBusinessCodeItems(['']);
  formData.promoters = [blankPromoter()];
  resetNameCheck();
  formRef.value?.resetValidation();
  expandedPanels.value = props.readOnly ? [] : ['details', 'clarification', 'promoters'];
};

const entity = ref('');

const loadSavedData = async (savedData: any) => {
  if (!savedData) return;

  if (savedData.category) formData.category = savedData.category;
  if (savedData.entityName) formData.entityName = savedData.entityName;
  if (savedData.purpose) formData.purpose = savedData.purpose;
  if (savedData.companyType) formData.companyType = savedData.companyType;
  if (savedData.subType) formData.subType = savedData.subType;
  if (savedData.suffix) formData.suffix = savedData.suffix;
  if (savedData.typeOfApplication) formData.typeOfApplication = savedData.typeOfApplication;

  if (savedData.clarification01) formData.clarification01 = savedData.clarification01;
  if (savedData.clarification02) formData.clarification02 = savedData.clarification02;
  if (savedData.clarification03) formData.clarification03 = savedData.clarification03;
  if (savedData.clarification04) formData.clarification04 = savedData.clarification04;
  if (savedData.clarification05) formData.clarification05 = savedData.clarification05;
  if (savedData.clarification07) formData.clarification07 = savedData.clarification07;
  if (Array.isArray(savedData.businessCodes) && savedData.businessCodes.length) {
    formData.businessCodes = [...savedData.businessCodes];
    setBusinessCodeItems(formData.businessCodes);
  }
  if (savedData.promoters && Array.isArray(savedData.promoters) && savedData.promoters.length) {
    // 빈 칸을 기본값으로 채워서 싣는다. T-Link 프리필로 들어온 프로모터는
    // 화면 전용 키(dateMenu/datePickerValue)가 없어서, 그대로 넣으면 날짜 칸이
    // undefined 에 바인딩된다.
    formData.promoters = savedData.promoters.map(
      (p: any) => ({ ...blankPromoter(), ...(p || {}) }) as PromoterInfoExtended,
    );
  }
};

defineExpose({
  validate,
  getValidationErrors,
  getFormData,
  resetForm,
  loadSavedData,
  entity,
  getSelectedCredentialId,
  ensureCredentialSelected,
  getNameCheckConflicts,
  isNameVerified,
  setCrsSubmissionId,
  restoreVerifiedName,
  getNameSignature,
});
</script>

<style scoped>
.text-uppercase :deep(input) {
  text-transform: uppercase;
}

:deep(.v-expansion-panel) {
  margin-bottom: 8px;
}

:deep(.v-expansion-panel-title) {
  min-height: 48px;
}

:deep(.v-expansion-panel-title--active) {
  min-height: 48px;
}
</style>
